import { Alert, Box, Button, Card, Snackbar, Typography } from '@mui/material'
import { ReactElement, useState } from 'react'
import axios from 'src/utilities/http-client'
import ConfirmAction from 'src/v3/components/confirm-action'
import { useIBSCProgress } from './hooks/use-ibsc-progress'
import IBSCProgressRing from './components/ibsc-progress-ring'
import IBSCProgramStats from './components/ibsc-program-stats'
import IBSCSummaryStats from './components/ibsc-summary-stats'
import IBSCCertificates from './components/ibsc-certificates'
import IBSCCourseList from './components/ibsc-course-list'

export default (): ReactElement => {
  let { data, isLoading, isError, refetch } = useIBSCProgress()
  let [resetDialog, setResetDialog] = useState<string>('')
  let [resetError, setResetError] = useState<string>('')

  let userID =
    data?.ibsc40.certificates[0]?.userID ??
    data?.ibsc100.certificates[0]?.userID

  let ibsc40Percent = data?.ibsc40.completionPercent ?? 0
  let ibsc100Percent = data?.ibsc100.completionPercent ?? 0

  if (isError) {
    return (
      <Card sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <Alert severity="error">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Unable to load IBSC progress data.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Try refreshing the page. If the problem persists, contact support.
          </Typography>
        </Alert>
      </Card>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
          IBSC Progress
        </Typography>
        <Typography color="text.secondary" variant="body1">
          Track your combined IBSC 40-Hour and 100-Hour renewal progress and
          view all earned certificates.
        </Typography>
      </Box>
      {/* Combined progress ring + per-program stats */}
      <Card
        sx={{ p: 3, backgroundColor: 'background.paper', mb: 3 }}
        aria-label="IBSC combined progress"
      >
        <IBSCProgressRing
          ibsc40Percent={ibsc40Percent}
          ibsc100Percent={ibsc100Percent}
          isLoading={isLoading}
        />
        <IBSCProgramStats data={data} isLoading={isLoading} />
      </Card>
      {/* Summary stats row */}
      <IBSCSummaryStats data={data} isLoading={isLoading} />
      {/* Disclaimers */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>IBSC 100:</strong> This content can be used for recertification
        of the CEN, CFRN, and CTRN. However, the certificates obtained upon
        completion will not be accepted by the BCEN. Providers must submit each
        individual certificate with their recertification.
      </Alert>
      <Alert severity="info" sx={{ mb: 2 }}>
        The following classes are included in this IBSC 40-hour refresher and
        CANNOT be utilized to fulfill any remaining education hours for your
        IBSC refresher: Acute Coronary Syndrome, Advanced Acid Base - Metabolic
        Acidosis &amp; Ventilation, Ambulance Safety, Animated Guide to ARDS,
        Applied Acid-Base, Arterial Lines, Cardiac Infections, Congenital Heart
        Defects, CVP, Flight Physiology, ICP Management - CNS Injury,
        Intra-aortic Balloon Pump, Lab Value, Labs - MISC, Maternal emergencies,
        Mechanical Ventilation, OB Emergencies, Pediatric Mechanical
        Ventilation, Pediatric Transport, Pulmonary Artery Catheters, Scene
        Factors, Sedation &amp; Analgesia, Septic Shock, Special Patient
        Populations (Born Different), Surgical Cases From Start to Finish, The
        Anatomy of Trauma, The RSI Summit, Traumatic Head Injuries and CT, and
        Understanding Pressors.
      </Alert>
      {/* All certificates across both programs */}
      <IBSCCertificates data={data} isLoading={isLoading} />
      {/* Course progress */}
      <Card sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="h6">Course Progress</Typography>
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => setResetDialog('reset')}
          >
            Reset Progress
          </Button>
        </Box>
        <ConfirmAction
          executeAction={() =>
            axios
              .post('/api/v2/profile/ibsc40/certification/reset', { userID })
              .then(() =>
                axios.post('/api/v2/profile/ibsc100/certification/reset', {
                  userID,
                }),
              )
              .then(() => refetch())
          }
          open={resetDialog}
          setOpen={setResetDialog}
          openText="reset"
          title="Confirm Progress Reset"
          text="Are you sure you want to reset your IBSC progress? This will reset both your IBSC 40-Hour and 100-Hour certifications."
          onFailure={(err: any) =>
            setResetError(err.message || 'Error resetting IBSC progress')
          }
        />
        <IBSCCourseList data={data} isLoading={isLoading} />
      </Card>
      <Snackbar open={!!resetError} onClose={() => setResetError('')}>
        <Alert
          variant="filled"
          onClose={() => setResetError('')}
          severity="error"
        >
          {resetError}
        </Alert>
      </Snackbar>
    </Box>
  )
}
